import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Check, X } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { PageHeader, AppButton, EmptyState, ConfirmModal } from "../ui";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import DataTable from "../Common/DataTable";
import { can } from "../../utils/access";
import "./attendance.scss";

/**
 * LeaveRequestsPage — admin review of student leave requests. Approving marks the
 * student 'excused' in the matching sessions (removed from the % denominator).
 * Route: /attendance/leaves (gated by attendanceManagement).
 */
const STATUS_TABS = ["pending", "approved", "rejected"];
const fmtDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");

const LeaveRequestsPage = () => {
  const navigate = useNavigate();
  const canReview = can("attendance.manage");
  const [status, setStatus] = useState("pending");
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState({ show: false, id: null, action: null });
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/attendance/leaves?status=${status}`);
      setLeaves(data.leaves || []);
    } catch {
      setToast({ show: true, message: "Failed to load leave requests.", type: "danger" });
    }
    setLoading(false);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const review = async () => {
    const { id, action } = confirm;
    setConfirm({ show: false, id: null, action: null });
    try {
      const { data } = await apiClient.put(`/attendance/leave/${id}`, { action });
      const extra = action === "approve" && data.leave?.excusedCount
        ? ` (${data.leave.excusedCount} session${data.leave.excusedCount !== 1 ? "s" : ""} excused)` : "";
      setToast({ show: true, message: `Leave ${action === "approve" ? "approved" : "rejected"}.${extra}`, type: "success" });
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Action failed.", type: "danger" });
    }
  };

  return (
    <div className="attendance-page sl-page">
      <PageHeader
        backButton
        onBack={() => navigate("/attendance")}
        icon={CalendarClock}
        title="Leave Requests"
        description="Review and approve student leave; approved leave is excused from attendance"
      />

      <div className="attendance-tabs mb-3">
        {STATUS_TABS.map((t) => (
          <button
            key={t}
            className={`attendance-tab ${status === t ? "attendance-tab--active" : ""}`}
            onClick={() => setStatus(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <Shimmer height={48} count={5} />
      ) : leaves.length === 0 ? (
        <EmptyState icon={CalendarClock} title={`No ${status} requests`} description="Leave requests will appear here." />
      ) : (
        <DataTable
          data={leaves}
          columns={[
            { key: "student", label: "Student", render: (l) => l.student?.name || "-" },
            { key: "dates", label: "Dates", render: (l) => `${fmtDate(l.fromDate)} – ${fmtDate(l.toDate)}` },
            { key: "batch", label: "Batch", render: (l) => l.batch?.name || "All batches" },
            { key: "reason", label: "Reason", render: (l) => l.reason || "—" },
            ...(status === "pending"
              ? (canReview ? [{
                  key: "actions",
                  label: "Actions",
                  render: (l) => (
                    <div className="attendance-actions">
                      <AppButton variant="outline" size="sm" onClick={() => setConfirm({ show: true, id: l._id, action: "approve" })}>
                        <Check size={14} /> Approve
                      </AppButton>
                      <AppButton variant="ghost" size="sm" onClick={() => setConfirm({ show: true, id: l._id, action: "reject" })}>
                        <X size={14} /> Reject
                      </AppButton>
                    </div>
                  ),
                }] : [])
              : [{ key: "reviewed", label: "Reviewed", render: (l) => (l.reviewedAt ? fmtDate(l.reviewedAt.slice(0, 10)) : "-") }]),
          ]}
        />
      )}

      <ConfirmModal
        show={confirm.show}
        onHide={() => setConfirm({ show: false, id: null, action: null })}
        onConfirm={review}
        title={confirm.action === "approve" ? "Approve leave?" : "Reject leave?"}
        message={
          confirm.action === "approve"
            ? "The student will be marked excused for the leave dates (removed from their attendance denominator)."
            : "This leave request will be rejected."
        }
        confirmText={confirm.action === "approve" ? "Approve" : "Reject"}
        variant={confirm.action === "approve" ? "success" : "danger"}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default LeaveRequestsPage;
